'use client';

import React, { useState, useEffect } from 'react';
import { Trophy, Clock, MousePointerClick, Calendar, Medal, History } from 'lucide-react';
import { formatTime, scoreTier } from '@/lib/gameLogic';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import dynamic from 'next/dynamic';

const ScoreboardChart = dynamic(() => import('./ScoreboardChart'), { ssr: false });

type FilterPeriod = 'all' | 'week' | 'today';

const FILTER_OPTIONS: { value: FilterPeriod; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'week', label: 'This Week' },
  { value: 'today', label: 'Today' },
];

type ScoreRow = {
  id: string;
  playerId: string;
  playerName: string;
  timeSeconds: number;
  moves: number;
  tier: string;
  completedAt: string;
};

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return (
    <div className="w-7 h-7 rounded-full bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center">
      <Trophy size={13} className="text-yellow-400" />
    </div>
  );
  if (rank === 2) return (
    <div className="w-7 h-7 rounded-full bg-slate-400/20 border border-slate-400/40 flex items-center justify-center">
      <Medal size={13} className="text-slate-300" />
    </div>
  );
  if (rank === 3) return (
    <div className="w-7 h-7 rounded-full bg-orange-500/20 border border-orange-500/40 flex items-center justify-center">
      <Medal size={13} className="text-orange-400" />
    </div>
  );
  return (
    <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
      <span className="font-mono text-[11px] font-bold text-muted-foreground">{rank}</span>
    </div>
  );
}

// Format date with time in Ulaanbaatar timezone (GMT+8)
function formatDateUB(iso: string): string {
  const d = new Date(iso);
  // GMT+8 offset
  const ubOffset = 8 * 60; // minutes
  const localOffset = d.getTimezoneOffset(); // minutes behind UTC (negative for ahead)
  const ubTime = new Date(d.getTime() + (ubOffset + localOffset) * 60 * 1000);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[ubTime.getMonth()];
  const day = ubTime.getDate();
  const hours = String(ubTime.getHours()).padStart(2, '0');
  const mins = String(ubTime.getMinutes()).padStart(2, '0');
  return `${month} ${day}, ${hours}:${mins}`;
}

function isWithinPeriod(iso: string, period: FilterPeriod): boolean {
  if (period === 'all') return true;
  const d = new Date(iso);
  if (period === 'today') {
    // Use Ulaanbaatar timezone (GMT+8) for "today" comparison
    const ubOffset = 8 * 60 * 60 * 1000;
    const nowUB = new Date(Date.now() + ubOffset);
    const dUB = new Date(d.getTime() + ubOffset);
    return (
      dUB.getUTCFullYear() === nowUB.getUTCFullYear() &&
      dUB.getUTCMonth() === nowUB.getUTCMonth() &&
      dUB.getUTCDate() === nowUB.getUTCDate()
    );
  }
  if (period === 'week') {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return d >= weekAgo;
  }
  return true;
}

// Deduplicate: keep only best score per player (lowest time, then fewest moves)
function getBestPerPlayer(rows: ScoreRow[]): ScoreRow[] {
  const bestMap = new Map<string, ScoreRow>();
  for (const row of rows) {
    const existing = bestMap.get(row.playerId);
    if (!existing) {
      bestMap.set(row.playerId, row);
    } else {
      const isBetter =
        row.timeSeconds < existing.timeSeconds ||
        (row.timeSeconds === existing.timeSeconds && row.moves < existing.moves);
      if (isBetter) bestMap.set(row.playerId, row);
    }
  }
  return Array.from(bestMap.values());
}

export default function ScoreboardTable() {
  const [filter, setFilter] = useState<FilterPeriod>('all');
  const [scores, setScores] = useState<ScoreRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, profile } = useAuth();

  useEffect(() => {
    async function fetchScores() {
      setLoading(true);
      const supabase = createClient();

      // Use RPC function that excludes admin scores at the database level
      // (SECURITY DEFINER ensures it works for both guests and logged-in users)
      const { data, error } = await supabase
        .rpc('get_non_admin_scores');

      if (!error && data) {
        setScores(
          (data as any[]).map((row: { id: string; player_id: string; player_name: string; time_seconds: number; moves: number; tier: string; completed_at: string }) => ({
            id: row.id,
            playerId: row.player_id,
            playerName: row.player_name,
            timeSeconds: row.time_seconds,
            moves: row.moves,
            tier: row.tier,
            completedAt: row.completed_at,
          }))
        );
      }
      setLoading(false);
    }
    fetchScores();
  }, []);

  const userId = user ? (user as { id: string }).id : null;

  // Personal best (single best score for current user)
  const personalBest = userId
    ? scores
        .filter((s) => s.playerId === userId)
        .sort((a, b) => {
          if (a.timeSeconds !== b.timeSeconds) return a.timeSeconds - b.timeSeconds;
          return a.moves - b.moves;
        })[0] ?? null
    : null;

  // Latest 5 games for current user (sorted by date desc)
  const personalHistory = userId
    ? scores
        .filter((s) => s.playerId === userId)
        .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime())
        .slice(0, 5)
    : [];

  // Leaderboard: best score per player, filtered by period, top 10
  const filtered = getBestPerPlayer(
    scores.filter((s) => isWithinPeriod(s.completedAt, filter))
  )
    .sort((a, b) => {
      if (a.timeSeconds !== b.timeSeconds) return a.timeSeconds - b.timeSeconds;
      return a.moves - b.moves;
    })
    .slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Personal best card */}
      {profile && personalBest && (
        <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 slide-up">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                <Trophy size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-[10px] font-semibold text-primary uppercase tracking-widest">Your Personal Best</p>
                <p className="text-sm font-bold text-foreground">{profile.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Time</p>
                <p className="font-mono text-lg font-bold text-foreground tabular-nums">{formatTime(personalBest.timeSeconds)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Moves</p>
                <p className="font-mono text-lg font-bold text-foreground tabular-nums">{personalBest.moves}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${scoreTier(personalBest.timeSeconds, personalBest.moves).className}`}>
                {personalBest.tier}
              </span>
            </div>
          </div>
        </div>
      )}

      {!user && (
        <div className="p-4 rounded-xl bg-muted/50 border border-border">
          <p className="text-sm text-muted-foreground">
            <a href="/sign-up-login-screen" className="text-primary font-semibold hover:underline">Sign in</a>
            {' '}to see your personal best and compete on the leaderboard
          </p>
        </div>
      )}

      {/* Personal Stats — latest 5 game history */}
      {profile && personalHistory.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 p-4 border-b border-border">
            <div className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <History size={13} className="text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">Personal Stats</h3>
              <p className="text-[10px] text-muted-foreground">Latest {personalHistory.length} games</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">#</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                    <div className="flex items-center gap-1"><Clock size={10} />Time</div>
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                    <div className="flex items-center gap-1"><MousePointerClick size={10} />Moves</div>
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Tier</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                    <div className="flex items-center gap-1"><Calendar size={10} />Date</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {personalHistory.map((entry, i) => {
                  const tier = scoreTier(entry.timeSeconds, entry.moves);
                  return (
                    <tr key={`ph-${entry.id}`} className="border-b border-border/40 hover:bg-muted/20 transition-colors duration-100">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="font-mono text-sm font-bold text-foreground tabular-nums">{formatTime(entry.timeSeconds)}</span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="font-mono text-sm text-foreground tabular-nums">{entry.moves}</span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-bold border ${tier.className}`}>
                          {tier.label}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-xs text-muted-foreground font-mono">{formatDateUB(entry.completedAt)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-1 gap-5">
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-foreground text-center">Global Stats</h3>
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-6">
              {[
                {
                  label: 'Total games played',
                  value: scores.length.toString(),
                  icon: <Trophy size={13} className="text-primary" />,
                },
                {
                  label: 'Fastest time ever',
                  value: scores.length > 0 ? formatTime(Math.min(...scores.map((s) => s.timeSeconds))) : '—',
                  icon: <Clock size={13} className="text-success" />,
                },
                {
                  label: 'Fewest moves ever',
                  value: scores.length > 0 ? String(Math.min(...scores.map((s) => s.moves))) : '—',
                  icon: <MousePointerClick size={13} className="text-warning" />,
                },
                {
                  label: 'ELITE tier achievers',
                  value: String(scores.filter((s) => s.tier === 'ELITE').length),
                  icon: <Medal size={13} className="text-yellow-400" />,
                },
              ].map((stat) => (
                <div key={`stat-${stat.label}`} className="flex flex-col items-center gap-1.5 min-w-[120px]">
                  <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                    {stat.icon}
                  </div>
                  <span className="font-mono text-xl font-bold text-foreground tabular-nums">{stat.value}</span>
                  <span className="text-[11px] text-muted-foreground text-center">{stat.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Leaderboard table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-border flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Trophy size={16} className="text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Global Leaderboard</h3>
            <span className="px-2 py-0.5 rounded bg-muted text-[10px] font-mono text-muted-foreground">
              Top {filtered.length}
            </span>
          </div>
          <div className="flex bg-muted rounded-lg p-0.5 gap-0.5">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={`filter-${opt.value}`}
                onClick={() => setFilter(opt.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150
                  ${filter === opt.value
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                  }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
              <Trophy size={20} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-semibold text-foreground">No scores for this period</p>
            <p className="text-xs text-muted-foreground mt-1">Play a game to appear on the leaderboard</p>
            <a
              href="/"
              className="mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-accent transition-all duration-150"
            >
              Play Now
            </a>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest w-12">Rank</th>
                  <th className="px-3 py-3 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Player</th>
                  <th className="px-3 py-3 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                    <div className="flex items-center gap-1"><Clock size={10} />Time</div>
                  </th>
                  <th className="px-3 py-3 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                    <div className="flex items-center gap-1"><MousePointerClick size={10} />Moves</div>
                  </th>
                  <th className="px-3 py-3 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Tier</th>
                  <th className="px-3 py-3 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                    <div className="flex items-center gap-1"><Calendar size={10} />Date</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry, i) => {
                  const rank = i + 1;
                  const isMe = userId && entry.playerId === userId;
                  const tier = scoreTier(entry.timeSeconds, entry.moves);

                  // Row highlight classes for top 3
                  let rowClass = 'border-b border-border/50 transition-colors duration-150 ';
                  if (rank === 1) {
                    rowClass += 'bg-yellow-500/10 border-l-2 border-l-yellow-500/60 hover:bg-yellow-500/15';
                  } else if (rank === 2) {
                    rowClass += 'bg-slate-400/10 border-l-2 border-l-slate-400/50 hover:bg-slate-400/15';
                  } else if (rank === 3) {
                    rowClass += 'bg-orange-500/10 border-l-2 border-l-orange-500/50 hover:bg-orange-500/15';
                  } else if (isMe) {
                    rowClass += 'bg-primary/5 hover:bg-primary/8';
                  } else {
                    rowClass += 'hover:bg-muted/30';
                  }

                  return (
                    <tr key={entry.id} className={rowClass}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <RankBadge rank={rank} />
                          {rank === 1 && <span className="text-[9px] font-bold text-yellow-400 uppercase tracking-wider hidden sm:block">1st</span>}
                          {rank === 2 && <span className="text-[9px] font-bold text-slate-300 uppercase tracking-wider hidden sm:block">2nd</span>}
                          {rank === 3 && <span className="text-[9px] font-bold text-orange-400 uppercase tracking-wider hidden sm:block">3rd</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${rank <= 3 ? 'ring-1 ring-offset-1 ring-offset-transparent ' + (rank === 1 ? 'ring-yellow-500/50' : rank === 2 ? 'ring-slate-400/50' : 'ring-orange-500/50') : ''}`}
                            style={{
                              background: `hsl(${(entry.playerName.charCodeAt(0) * 47) % 360}, 60%, 25%)`,
                              color: `hsl(${(entry.playerName.charCodeAt(0) * 47) % 360}, 80%, 70%)`,
                            }}
                          >
                            {entry.playerName.slice(0, 2).toUpperCase()}
                          </div>
                          <div className="flex flex-col">
                            <span className={`text-sm font-medium ${rank <= 3 ? 'font-semibold text-foreground' : 'text-foreground'}`}>{entry.playerName}</span>
                            {isMe && (
                              <span className="text-[9px] text-primary font-semibold">You</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className={`font-mono text-sm font-bold tabular-nums ${rank === 1 ? 'text-yellow-400' : rank === 2 ? 'text-slate-300' : rank === 3 ? 'text-orange-400' : 'text-foreground'}`}>
                          {formatTime(entry.timeSeconds)}
                        </span>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className="font-mono text-sm text-foreground tabular-nums">{entry.moves}</span>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${tier.className}`}>
                          {entry.tier}
                        </span>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className="text-xs text-muted-foreground font-mono">{formatDateUB(entry.completedAt)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}